import { joinGameUrl } from '@words-happen/app-urls';
import { NewGameScreen, type NewGameScreenState } from '@words-happen/screens';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { createGamesApi, type GamesApi, lobbyFailure } from '../../api/games';
import { ROUTES } from '../../routes';
import { useSession } from '../../session';
import { useLobbyFailure } from './use-lobby-failure';

/** What the player reads when the link couldn't be put on the clipboard. */
export const COPY_FAILED_MESSAGE = "Couldn't copy the link. Select it and copy it yourself.";

export interface NewGamePageProps {
  /** Replaces words-happen-gql's lobby API. */
  gamesApi?: GamesApi;
  /** The game app's origin, for the invite link. Defaults to this build's environment. */
  playOrigin?: string;
  /** Puts text on the clipboard. Defaults to the browser's. */
  copyText?: (text: string) => Promise<void>;
  /** Opens the device's share sheet; null where it can't. Defaults to the browser's `navigator.share`. */
  shareLink?: ShareLink | null;
}

/** Shares an invitation; rejects with an `AbortError` when the player closes the share sheet. */
export type ShareLink = (invitation: { code: string; link: string }) => Promise<void>;

const browserCopy = (text: string) => navigator.clipboard.writeText(text);

const browserShare = (): ShareLink | null =>
  typeof navigator !== 'undefined' && typeof navigator.share === 'function'
    ? ({ code, link }) =>
        navigator.share({ title: 'Words Happen', text: `Play me at Words Happen! Invite code ${code}`, url: link })
    : null;

const isAbort = (error: unknown) => error instanceof DOMException && error.name === 'AbortError';

const NewGame: React.FC<Required<NewGamePageProps>> = ({ gamesApi, playOrigin, copyText, shareLink }) => {
  const navigate = useNavigate();
  const handleFailure = useLobbyFailure();
  const [search] = useSearchParams();
  const existingGame = search.get('game');
  const [state, setState] = useState<NewGameScreenState>({
    kind: 'choose',
    pending: existingGame ? 'invite' : undefined,
  });
  const [error, setError] = useState<string>();
  const asked = useRef(false);

  const showInvitation = async (gameId: string) => {
    const code = await gamesApi.inviteCode(gameId);
    setState({ kind: 'invite', code, link: joinGameUrl(playOrigin, code), copied: false });
  };

  /** At the game limit, finds the next step: the most recent game on the player's turn, if there is one. */
  const offerNextGame = async (message: string) => {
    setState({ kind: 'too-many-games', message, nextGame: null });
    try {
      const next = (await gamesApi.myGames()).active.find(({ isMyTurn }) => isMyTurn);
      if (next) {
        setState({ kind: 'too-many-games', message, nextGame: { opponentName: next.opponentName ?? '' } });
      }
    } catch {
      // Without the list, Go to your games is still offered.
    }
  };

  const fail = (caught: unknown) => {
    const message = handleFailure(caught, ROUTES.newGame);
    if (message !== undefined && lobbyFailure(caught).kind === 'tooManyGames') {
      setError(undefined);
      void offerNextGame(message);
      return;
    }
    setState({ kind: 'choose' });
    setError(message);
  };

  // Share invite on /games opens /games/new?game=<id>: the invitation of a game already waiting.
  // biome-ignore lint/correctness/useExhaustiveDependencies: asked once, for the game in the URL it opened with
  useEffect(() => {
    if (existingGame && !asked.current) {
      asked.current = true;
      showInvitation(existingGame).catch(fail);
    }
  }, [existingGame]);

  const invite = async () => {
    setError(undefined);
    setState({ kind: 'choose', pending: 'invite' });
    try {
      await showInvitation((await gamesApi.createGame()).id);
    } catch (caught) {
      fail(caught);
    }
  };

  const quickMatch = async () => {
    setError(undefined);
    setState({ kind: 'choose', pending: 'quick-match' });
    try {
      const game = await gamesApi.quickMatch();
      setState(
        game.status === 'active' && game.opponentName
          ? { kind: 'matched', opponentName: game.opponentName }
          : { kind: 'waiting-for-match' },
      );
    } catch (caught) {
      fail(caught);
    }
  };

  const copyLink = async () => {
    if (state.kind !== 'invite') return;
    try {
      await copyText(state.link);
      setError(undefined);
      setState({ ...state, copied: true });
    } catch {
      setError(COPY_FAILED_MESSAGE);
    }
  };

  const share = async () => {
    if (state.kind !== 'invite' || !shareLink) return;
    try {
      await shareLink(state);
      setError(undefined);
    } catch (caught) {
      // Closing the share sheet is a choice, not a failure; anything else falls back to the clipboard.
      if (!isAbort(caught)) await copyLink();
    }
  };

  return (
    <NewGameScreen
      state={state}
      error={error}
      onInvite={() => void invite()}
      onQuickMatch={() => void quickMatch()}
      onCopyLink={() => void copyLink()}
      onShare={shareLink ? () => void share() : undefined}
      onShowGames={() => navigate(ROUTES.games)}
      // The games list puts this game first, under Your turn, until the game screen (WH-156) can open it by ID.
      onPlayNext={() => navigate(ROUTES.games)}
      onBack={() => navigate(ROUTES.games)}
    />
  );
};

/**
 * The new-game screen, /games/new (WH-155): Invite a friend (createGame, then inviteToGame for the link and code) or
 * Quick match. The invitation is shared through the device's share sheet where the browser has one (`navigator.share`:
 * phones, Safari), and copied otherwise. At the game limit both choices are disabled and the next step is offered:
 * the player's turn in a game they have, or else their games, to finish or cancel one. With `?game=<id>` it shows the invitation of that waiting game. A player
 * with no display name is sent to choose one and brought back. Signed out, it goes to /welcome.
 */
export const NewGamePage: React.FC<NewGamePageProps> = ({
  gamesApi,
  playOrigin = PLAY_URL,
  copyText = browserCopy,
  shareLink,
}) => {
  const { signedIn } = useSession();
  const [api] = useState(() => gamesApi ?? createGamesApi());
  const [share] = useState(() => (shareLink === undefined ? browserShare() : shareLink));

  return signedIn ? (
    <NewGame gamesApi={api} playOrigin={playOrigin} copyText={copyText} shareLink={share} />
  ) : (
    <Navigate replace to={ROUTES.welcome} />
  );
};
